import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Modal from '../Modal';
import { Badge, EmptyState, Icon, SearchInput, Tabs } from '../ui';
import { ago } from '../../observability/contract';
import { loadJobRuns } from '../../observability/client';
import { clock, duration } from './units';

/* ── Jobs: every scheduled job and service loop, and what each did ──
   One row per job (GET /api/observability/jobs). Clicking a job opens its run history.
   A loop's routine ticks share one run row per quarter hour, so a janitor that ticks
   every minute reads as one row saying "15 runs, 42 rows deleted", not fifteen. */

const JOB_STATUS = {
  ok: { tone: 'ok', label: 'On schedule' },
  running: { tone: 'info', label: 'Running' },
  failing: { tone: 'danger', label: 'Failing' },
  interrupted: { tone: 'warn', label: 'Interrupted' },
  overdue: { tone: 'warn', label: 'Overdue' },
  skipped: { tone: 'neutral', label: 'Skipped' },
  never: { tone: 'neutral', label: 'Never run' },
  // No run yet and the first scheduled one is still ahead (a Data Governance category).
  pending: { tone: 'neutral', label: 'First run pending' },
  disabled: { tone: 'neutral', label: 'Switched off' },
  unrecorded: { tone: 'outline', label: 'Not recorded' },
};
const ATTENTION = new Set(['failing', 'interrupted', 'overdue']);

const RUN_STATUS = {
  ok: { tone: 'ok', label: 'OK' },
  error: { tone: 'danger', label: 'Failed' },
  skipped: { tone: 'neutral', label: 'Skipped' },
  running: { tone: 'info', label: 'Running' },
  interrupted: { tone: 'warn', label: 'Interrupted' },
};

const GROUP_ORDER = ['Scheduled tasks', 'Service loops', 'Kubernetes CronJobs', 'Other'];
const SOURCE_LABEL = {
  EventBridge: 'EventBridge schedule',
  CronJob: 'CronJob schedule',
  'Data Governance': 'set on Data Governance',
  observed: 'as observed',
  default: 'expected',
};
const RUNS_PAGE = 20;

function every(seconds) {
  const s = Number(seconds) || 0;
  if (!s) return 'irregular';
  if (s === 60) return 'every minute';
  if (s < 3600) return `every ${Math.round(s / 60)} min`;
  if (s === 3600) return 'every hour';
  if (s < 86400) return `every ${Math.round(s / 3600)} h`;
  if (s === 86400) return 'daily';
  if (s === 7 * 86400) return 'weekly';
  return `every ${Math.round(s / 86400)} d`;
}

function took(ms) {
  if (ms == null) return '—';
  if (ms < 1000) return `${ms} ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(ms < 10000 ? 1 : 0)} s`;
  return duration(ms / 1000);
}

const humanKey = (k) => String(k).replace(/_/g, ' ');

function figure(value) {
  if (typeof value === 'number') return value.toLocaleString();
  if (typeof value === 'boolean') return value ? 'yes' : 'no';
  return String(value);
}

// A run's summary as short "label: value" pairs. A nested object (rows deleted per table)
// reads as one pair per entry. ISO timestamps (the silent-turn window) are shown as times.
function SummaryChips({ summary }) {
  if (!summary || typeof summary !== 'object') return <span className="text-faint">—</span>;
  const pairs = [];
  Object.entries(summary).forEach(([k, v]) => {
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      Object.entries(v).forEach(([k2, v2]) => pairs.push([`${humanKey(k)}, ${k2}`, v2]));
    } else if (typeof v === 'string' && /^\d{4}-\d\d-\d\dT/.test(v)) {
      pairs.push([humanKey(k), clock(Date.parse(v))]);
    } else {
      pairs.push([humanKey(k), Array.isArray(v) ? v.join(', ') : v]);
    }
  });
  if (!pairs.length) return <span className="text-faint">—</span>;
  return (
    <span className="row row-wrap gap-1">
      {pairs.map(([k, v]) => (
        <Badge key={k} tone={v === 0 || v === '0' ? 'outline' : 'neutral'}>
          {k}: {figure(v)}
        </Badge>
      ))}
    </span>
  );
}

function ScheduleText({ schedule }) {
  if (!schedule) return <span className="text-faint">—</span>;
  const src = SOURCE_LABEL[schedule.source];
  return (
    <>
      <span>{schedule.when || every(schedule.every_s)}</span>
      {(schedule.expression || src) && (
        <span className="cell-sub">
          {schedule.expression && <span className="mono">{schedule.expression}</span>}
          {schedule.expression && src && ' · '}
          {src}
        </span>
      )}
      {schedule.next_at && <span className="cell-sub">next {clock(schedule.next_at)}</span>}
    </>
  );
}

function RunHistory({ job }) {
  const [runs, setRuns] = useState([]);
  const [next, setNext] = useState(null);
  const [source, setSource] = useState(null);
  const [keeps, setKeeps] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadPage = useCallback(async (before) => {
    setLoading(true);
    setError(null);
    try {
      const body = await loadJobRuns(job.key, { limit: RUNS_PAGE, before });
      setRuns(prev => (before ? [...prev, ...body.runs] : body.runs));
      setNext(body.next_before);
      setSource(body.source);
      setKeeps(body.keeps || null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [job.key]);

  useEffect(() => { loadPage(null); }, [loadPage]);

  if (error && !runs.length) {
    return (
      <div className="notice notice-danger" role="alert">
        <Icon name="alertCircle" />
        <div className="notice-body">The run history did not load: {error}</div>
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => loadPage(null)}>Try again</button>
      </div>
    );
  }
  if (!runs.length) {
    return loading
      ? <p className="text-muted" role="status">Loading the run history…</p>
      : <EmptyState icon="history" title="No runs recorded">This job has not recorded a run yet.</EmptyState>;
  }
  return (
    <>
      {source === 'kubernetes' && keeps && (
        <p className="text-sm text-muted mt-0">
          Kubernetes keeps the last {keeps.ok} successful and {keeps.error} failed {keeps.error === 1 ? 'run' : 'runs'} of a
          CronJob, so this is all the history there is.
        </p>
      )}
      <div className="table-container bordered">
        <table className="table table-compact table-stack">
          <thead>
            <tr>
              <th scope="col">Started</th>
              <th scope="col">Result</th>
              <th scope="col" className="num">Runs</th>
              <th scope="col" className="num">Took</th>
              <th scope="col">What it did</th>
            </tr>
          </thead>
          <tbody>
            {runs.map(r => {
              const st = RUN_STATUS[r.status] || { tone: 'neutral', label: r.status };
              const shared = r.runs > 1;
              return (
                <tr key={r.id}>
                  <td data-label="Started" className="nowrap">
                    <span title={clock(r.started_at)}>{ago(r.started_at)}</span>
                    <span className="cell-sub">
                      {clock(r.started_at)}
                      {shared && r.finished_at ? ` to ${clock(r.finished_at)}` : ''}
                    </span>
                  </td>
                  <td data-label="Result">
                    <Badge tone={st.tone} dot>{st.label}</Badge>
                    {r.trigger === 'manual' && <span className="cell-sub">run by hand</span>}
                    {r.error && <span className="cell-sub text-danger" style={{ whiteSpace: 'normal' }}>{r.error}</span>}
                  </td>
                  <td data-label="Runs" className="num">{r.runs}</td>
                  <td data-label="Took" className="num nowrap" title={shared ? 'The slowest of these runs' : undefined}>
                    {took(r.duration_ms)}
                  </td>
                  <td data-label="What it did"><SummaryChips summary={r.summary} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {next && (
        <div className="row mt-3">
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => loadPage(next)} disabled={loading}>
            {loading ? <span className="spinner-sm" /> : <Icon name="history" />}
            Older runs
          </button>
        </div>
      )}
      {error && runs.length > 0 && <p className="text-sm text-danger mt-2" role="alert">{error}</p>}
    </>
  );
}

function JobDialog({ job, onClose }) {
  const st = JOB_STATUS[job.status] || { tone: 'neutral', label: job.status };
  return (
    <Modal open onClose={onClose} title={job.label} maxWidth={960}
           description={job.description}>
      <dl className="kv mb-4">
        <dt>Status</dt>
        <dd><Badge tone={st.tone} dot>{st.label}</Badge></dd>
        <dt>Runs in</dt>
        <dd>{job.runs_in || '—'}</dd>
        <dt>Schedule</dt>
        <dd>
          <ScheduleText schedule={job.schedule} />
          {job.schedule?.rule && <span className="cell-sub mono">{job.schedule.rule}</span>}
        </dd>
        <dt>Last success</dt>
        <dd>{job.last_ok_at ? `${ago(job.last_ok_at)}, ${clock(job.last_ok_at)}` : 'none recorded'}</dd>
        <dt>Job name</dt>
        <dd className="mono">{job.key}</dd>
      </dl>
      {job.history ? <RunHistory job={job} /> : (
        <p className="text-muted">This schedule's target does not record its runs, so there is no history to show.</p>
      )}
    </Modal>
  );
}

function StatusSummary({ jobs }) {
  const counts = {};
  jobs.forEach(j => { counts[j.status] = (counts[j.status] || 0) + 1; });
  const bad = ['failing', 'interrupted', 'overdue'].filter(s => counts[s]);
  // Only a problem earns a banner. A healthy page, or one whose jobs have simply not run
  // yet, already says so row by row in the Status column and the "Needs attention" tab.
  if (!bad.length) return null;
  return (
    <div className="notice notice-warn mb-3" role="status">
      <Icon name="alert" />
      <div className="notice-body">
        <strong>{bad.map(s => `${counts[s]} ${JOB_STATUS[s].label.toLowerCase()}`).join(', ')}.</strong> Open a job
        to see its runs and the error of the last one.
      </div>
    </div>
  );
}

export default function JobsPanel({ data }) {
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [openKey, setOpenKey] = useState(null);
  const jobs = useMemo(() => data?.jobs || [], [data]);
  const needs = jobs.filter(j => ATTENTION.has(j.status)).length;

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return jobs.filter(j => (filter === 'all' || (filter === 'attention' ? ATTENTION.has(j.status) : j.group === filter))
      && (!q || `${j.label} ${j.key} ${j.runs_in}`.toLowerCase().includes(q)));
  }, [jobs, filter, search]);

  const groups = useMemo(() => {
    const present = [...new Set(jobs.map(j => j.group))];
    return [...GROUP_ORDER.filter(g => present.includes(g)), ...present.filter(g => !GROUP_ORDER.includes(g))];
  }, [jobs]);
  const open = jobs.find(j => j.key === openKey) || null;

  if (!jobs.length) {
    return (
      <div className="card">
        <EmptyState icon="clock" title="No jobs found">Nothing on this environment records scheduled work yet.</EmptyState>
      </div>
    );
  }

  const tabs = [
    { value: 'all', label: 'All', count: jobs.length },
    { value: 'attention', label: 'Needs attention', count: needs },
    ...groups.map(g => ({ value: g, label: g })),
  ];

  return (
    <>
      <StatusSummary jobs={jobs} />
      <div className="card card-flush mb-4">
        <div className="toolbar row-wrap">
          <Tabs size="sm" label="Show jobs" value={filter} onChange={setFilter} tabs={tabs} />
          <div className="toolbar-spacer" />
          <SearchInput value={search} onChange={setSearch} placeholder="Find a job" label="Find a job"
                       style={{ width: 220 }} />
        </div>
        <div className="table-container">
          <table className="table table-stack">
            <thead>
              <tr>
                <th scope="col">Job</th>
                <th scope="col">Runs in</th>
                <th scope="col">Schedule</th>
                <th scope="col">Last run</th>
                <th scope="col" className="num">Last 24 h</th>
                <th scope="col">Status</th>
              </tr>
            </thead>
            {groups.map(group => {
              const members = rows.filter(j => j.group === group);
              if (!members.length) return null;
              return (
                <tbody key={group}>
                  <tr className="table-group-row">
                    <th scope="colgroup" colSpan={6}>{group}</th>
                  </tr>
                  {members.map(j => {
                    const st = JOB_STATUS[j.status] || { tone: 'neutral', label: j.status };
                    const last = j.last_run;
                    const at = last && (last.finished_at || last.started_at);
                    const day = j.stats_24h;
                    return (
                      <tr key={j.key}>
                        <td data-label="Job">
                          <button type="button" className="btn btn-link text-left" onClick={() => setOpenKey(j.key)}
                                  aria-haspopup="dialog">
                            {j.label}
                          </button>
                          <span className="cell-sub mono">{j.key}</span>
                        </td>
                        <td data-label="Runs in">{j.runs_in || '—'}</td>
                        <td data-label="Schedule"><ScheduleText schedule={j.schedule} /></td>
                        <td data-label="Last run" className="nowrap">
                          {at ? (
                            <>
                              <span title={clock(at)}>{ago(at)}</span>
                              <span className="cell-sub">
                                {(RUN_STATUS[last.status] || { label: last.status }).label}
                                {last.duration_ms != null && `, ${took(last.duration_ms)}`}
                              </span>
                            </>
                          ) : <span className="text-faint">—</span>}
                        </td>
                        <td data-label="Last 24 h" className="num nowrap">
                          {day ? (
                            <>
                              {day.runs.toLocaleString()} {day.runs === 1 ? 'run' : 'runs'}
                              {day.errors > 0 && <span className="cell-sub text-danger">{day.errors} failed</span>}
                            </>
                          ) : <span className="text-faint">—</span>}
                        </td>
                        <td data-label="Status"><Badge tone={st.tone} dot>{st.label}</Badge></td>
                      </tr>
                    );
                  })}
                </tbody>
              );
            })}
          </table>
          {!rows.length && (
            <EmptyState icon="search" title="No job matches">Try another filter or search.</EmptyState>
          )}
        </div>
      </div>
      {open && <JobDialog job={open} onClose={() => setOpenKey(null)} />}
    </>
  );
}
